import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PhoneCall, CheckCircle2, Clock, ShieldCheck, Mail } from 'lucide-react';
export function CTA() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section id="contact" className="py-32 bg-cream relative" data-id="element-73">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-74">
        <motion.div className="bg-white rounded-2xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] overflow-hidden border border-gray-100" initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-75">
          <div className="grid grid-cols-1 lg:grid-cols-5" data-id="element-76">
            {/* Left Content */}
            <motion.div className="lg:col-span-3 p-12 md:p-16 lg:p-20 relative overflow-hidden" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} data-id="element-77">
              {/* Decorative background element */}
              <div className="absolute -top-10 -left-10 text-[15rem] font-heading font-black text-gray-50/50 select-none pointer-events-none leading-none z-0" data-id="element-78">
                01
              </div>

              {/* Left accent line */}
              <div className="absolute left-0 top-12 bottom-12 w-1 bg-gradient-to-b from-gold/0 via-gold to-gold/0 opacity-30" data-id="element-79"></div>

              <div className="relative z-10" data-id="element-80">
                <motion.div variants={itemVariants} className="flex items-center gap-4 mb-6" data-id="element-81">
                  <div className="h-px w-12 bg-gold" data-id="element-82"></div>
                  <span className="text-gold font-bold uppercase tracking-widest text-xs" data-id="element-83">
                    Take The First Step
                  </span>
                </motion.div>

                <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-heading font-bold text-navy mb-8 leading-tight" data-id="element-84">
                  Your Dream Home Is{' '}
                  <span className="text-navy italic font-light relative inline-block" data-id="element-85">
                    One Call Away
                    <span className="absolute -bottom-2 left-0 w-full h-1 bg-gold/30 rounded-full" data-id="element-86"></span>
                  </span>
                </motion.h2>

                <motion.div variants={itemVariants} className="space-y-6 text-lg text-muted mb-8 font-light leading-relaxed" data-id="element-87">
                  <p data-id="element-88">You've been thinking about it long enough.</p>
                  <p data-id="element-89">
                    That kitchen renovation. That finished basement. That new
                    roof or beautiful deck. The fresh exterior that makes your
                    home the one everyone notices on the block.
                  </p>
                  <p className="font-heading font-bold text-navy text-2xl pt-4 pb-2 relative inline-block" data-id="element-90">
                    It's time to make it real.
                    <span className="absolute bottom-1 left-0 w-full h-3 bg-gold/20 -z-10 -rotate-1" data-id="element-91"></span>
                  </p>
                </motion.div>

                {/* Benefits List */}
                <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10" data-id="element-92">
                  <div className="flex items-center gap-3 text-sm text-navy font-medium" data-id="element-93">
                    <CheckCircle2 className="w-5 h-5 text-gold" data-id="element-94" />
                    <span data-id="element-95">100% Free Consultation</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-navy font-medium" data-id="element-96">
                    <CheckCircle2 className="w-5 h-5 text-gold" data-id="element-97" />
                    <span data-id="element-98">Zero Obligation</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-navy font-medium" data-id="element-99">
                    <ShieldCheck className="w-5 h-5 text-gold" data-id="element-100" />
                    <span data-id="element-101">Fully Licensed & Insured</span>
                  </div>
                </motion.div>

                <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-6 mb-4" data-id="element-102">
                  <motion.a href="tel:2158245130" whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} className="flex items-center justify-center gap-3 bg-navy hover:bg-navy-800 text-white font-bold px-8 py-4 rounded-sm transition-colors text-sm uppercase tracking-widest shadow-lg" data-id="element-103">
                    <PhoneCall size={18} className="text-gold" data-id="element-104" />
                    (215) 824-5130
                  </motion.a>
                  <motion.button whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} className="bg-gold hover:bg-white text-navy font-bold px-8 py-4 rounded-sm transition-colors uppercase tracking-widest text-sm border border-transparent hover:border-gold shadow-[0_0_15px_rgba(201,168,76,0.2)] hover:shadow-[0_0_20px_rgba(201,168,76,0.4)]" data-id="element-105">
                    Get Free Estimate
                  </motion.button>
                </motion.div>

                <motion.a variants={itemVariants} href="mailto:blueskycontractorllc23@gmail.com" className="inline-flex items-center gap-2 text-sm text-muted hover:text-navy transition-colors mb-6" data-id="element-106">
                  <Mail size={16} className="text-gold" data-id="element-107" />
                  blueskycontractorllc23@gmail.com
                </motion.a>

                {/* Trust Indicators */}
                <motion.div variants={itemVariants} className="flex items-center gap-6 text-xs text-muted font-medium uppercase tracking-wider" data-id="element-108">
                  <div className="flex items-center gap-2" data-id="element-109">
                    <Clock className="w-4 h-4 text-gold" data-id="element-110" />
                    <span data-id="element-111">Estimates within 24hrs</span>
                  </div>
                  <div className="hidden sm:flex items-center gap-2" data-id="element-112">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold" data-id="element-113"></div>
                    <span data-id="element-114">500+ Homes Transformed</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Right Accent */}
            <div className="lg:col-span-2 bg-navy p-12 flex flex-col justify-center relative overflow-hidden" data-id="element-115">
              <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80')] bg-cover bg-center mix-blend-overlay" data-id="element-116"></div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-117"></div>

              <div className="relative z-10 text-center" data-id="element-118">
                <img src="https://cdn.magicpatterns.com/uploads/mHQYWDhNJXGSPsTWooPTGm/Photoroom_20260425_171611.png" alt="Blue Sky Logo" className="w-32 h-auto mx-auto mb-10 opacity-90" data-id="element-119" />
                <h3 className="text-2xl font-heading font-bold text-white mb-3 tracking-widest" data-id="element-120">
                  BLUE SKY
                </h3>
                <p className="text-gold text-xs uppercase tracking-[0.2em] mb-10" data-id="element-121">
                  General Contractor LLC
                </p>

                <div className="space-y-4 text-sm text-gray-400 font-light tracking-wide" data-id="element-122">
                  <p data-id="element-123">Building Dreams Across Pennsylvania</p>
                  <p className="text-white/50" data-id="element-124">PA License #934782</p>
                  <p className="text-white/50" data-id="element-125">OSHA Certified #14-602163933</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}